import {invalidateOnSuccess, omneticApi} from '@dms/api/base';
import {UuidResponseBodySchema} from '@dms/api/shared';

import {
  CreateWarrantyProgramApiArg,
  CreateWarrantyProgramApiResponse,
  CreateWarrantyTypeApiArg,
  CreateWarrantyTypeApiResponse,
  DeleteWarrantyProgramApiArg,
  DeleteWarrantyTypeApiArg,
  GetWarrantyProgramApiArg,
  GetWarrantyTypeApiArg,
  ListWarrantyProgramApiResponse,
  ListWarrantyProgramApiResponseSchema,
  ListWarrantyTypeApiResponse,
  ListWarrantyTypeApiResponseSchema,
  SalesWarrantySettingsRequestBodySchema,
  SalesWarrantySettingsResponseBody,
  SalesWarrantySettingsResponseBodySchema,
  SetSalesWarrantySettingsApiArg,
  UpdateWarrantyProgramApiArg,
  UpdateWarrantyTypeApiArg,
  WarrantyProgramRequestBodySchema,
  WarrantyProgramResponseBody,
  WarrantyProgramResponseBodySchema,
  WarrantyTypeRequestBodySchema,
  WarrantyTypeResponseBody,
  WarrantyTypeResponseBodySchema,
  AssignVehicleWarrantyApiArg,
  AssignVehicleWarrantyApiResponse,
  AssignWarrantyProductRequestBodySchema,
  ListVehicleWarrantiesApiArg,
  ListVehicleWarrantiesApiResponse,
  ListVehicleWarrantiesApiResponseSchema,
  RemoveVehicleWarrantyApiArg,
  UpdateVehicleWarrantyApiArg,
  UpdateWarrantyAssignmentRequestBodySchema,
} from './types';

export const warrantyApi = omneticApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (build) => ({
    listWarrantyProgram: build.query<ListWarrantyProgramApiResponse, void>({
      query: () => ({url: '/dms/v1/warranty-program'}),
      providesTags: ['WarrantyProgramList'],
      extraOptions: {
        responseSchema: ListWarrantyProgramApiResponseSchema,
      },
    }),
    getWarrantyProgram: build.query<WarrantyProgramResponseBody, GetWarrantyProgramApiArg>({
      query: (queryArg) => ({url: `/dms/v1/warranty-program/${queryArg.warrantyProgramId}`}),
      providesTags: (result, error, queryArg) => [
        {type: 'WarrantyProgram', id: queryArg.warrantyProgramId},
      ],
      extraOptions: {
        responseSchema: WarrantyProgramResponseBodySchema,
      },
    }),
    createWarrantyProgram: build.mutation<
      CreateWarrantyProgramApiResponse,
      CreateWarrantyProgramApiArg
    >({
      query: (queryArg) => ({
        url: '/dms/v1/warranty-program',
        method: 'POST',
        body: queryArg.createWarrantyProgramRequestBody,
      }),
      invalidatesTags: invalidateOnSuccess(['WarrantyProgramList']),
      extraOptions: {
        requestSchema: WarrantyProgramRequestBodySchema,
        responseSchema: UuidResponseBodySchema,
      },
    }),
    updateWarrantyProgram: build.mutation<void, UpdateWarrantyProgramApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/warranty-program/${queryArg.warrantyProgramId}`,
        method: 'PUT',
        body: queryArg.updateWarrantyProgramRequestBody,
      }),
      invalidatesTags: invalidateOnSuccess((result, queryArg: UpdateWarrantyProgramApiArg) => [
        {type: 'WarrantyProgram', id: queryArg.warrantyProgramId},
        'WarrantyProgramList',
      ]),
      extraOptions: {
        requestSchema: WarrantyProgramRequestBodySchema,
      },
    }),
    deleteWarrantyProgram: build.mutation<void, DeleteWarrantyProgramApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/warranty-program/${queryArg.warrantyProgramId}`,
        method: 'DELETE',
      }),
      invalidatesTags: invalidateOnSuccess((result, queryArg: DeleteWarrantyProgramApiArg) => [
        {type: 'WarrantyProgram', id: queryArg.warrantyProgramId},
        'WarrantyProgramList',
      ]),
    }),
    getSalesWarrantySettings: build.query<SalesWarrantySettingsResponseBody, void>({
      query: () => ({url: '/dms/v1/settings/sales/warranty'}),
      providesTags: ['SalesWarrantySettings'],
      extraOptions: {
        responseSchema: SalesWarrantySettingsResponseBodySchema,
      },
    }),
    setSalesWarrantySettings: build.mutation<void, SetSalesWarrantySettingsApiArg>({
      query: (queryArg) => ({
        url: '/dms/v1/settings/sales/warranty',
        method: 'PUT',
        body: queryArg.salesWarrantySettingsRequestBody,
      }),
      invalidatesTags: invalidateOnSuccess(['SalesWarrantySettings']),
      extraOptions: {
        requestSchema: SalesWarrantySettingsRequestBodySchema,
      },
    }),
    listWarrantyType: build.query<ListWarrantyTypeApiResponse, void>({
      query: () => ({url: '/dms/v1/warranty-type'}),
      providesTags: ['WarrantyTypeList'],
      extraOptions: {
        responseSchema: ListWarrantyTypeApiResponseSchema,
      },
    }),
    getWarrantyType: build.query<WarrantyTypeResponseBody, GetWarrantyTypeApiArg>({
      query: (queryArg) => ({url: `/dms/v1/warranty-type/${queryArg.warrantyTypeId}`}),
      providesTags: (result, error, queryArg) => [
        {type: 'WarrantyType', id: queryArg.warrantyTypeId},
      ],
      extraOptions: {
        responseSchema: WarrantyTypeResponseBodySchema,
      },
    }),
    createWarrantyType: build.mutation<CreateWarrantyTypeApiResponse, CreateWarrantyTypeApiArg>({
      query: (queryArg) => ({
        url: '/dms/v1/warranty-type',
        method: 'POST',
        body: queryArg.createWarrantyTypeRequestBody,
      }),
      invalidatesTags: invalidateOnSuccess(['WarrantyTypeList']),
      extraOptions: {
        requestSchema: WarrantyTypeRequestBodySchema,
        responseSchema: UuidResponseBodySchema,
      },
    }),
    updateWarrantyType: build.mutation<void, UpdateWarrantyTypeApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/warranty-type/${queryArg.warrantyTypeId}`,
        method: 'PUT',
        body: queryArg.updateWarrantyTypeRequestBody,
      }),
      invalidatesTags: invalidateOnSuccess((result, queryArg: UpdateWarrantyTypeApiArg) => [
        {type: 'WarrantyType', id: queryArg.warrantyTypeId},
        'WarrantyTypeList',
      ]),
      extraOptions: {
        requestSchema: WarrantyTypeRequestBodySchema,
      },
    }),
    deleteWarrantyType: build.mutation<void, DeleteWarrantyTypeApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/warranty-type/${queryArg.warrantyTypeId}`,
        method: 'DELETE',
      }),
      invalidatesTags: invalidateOnSuccess((result, queryArg: DeleteWarrantyTypeApiArg) => [
        {type: 'WarrantyType', id: queryArg.warrantyTypeId},
        'WarrantyTypeList',
      ]),
    }),
    listVehicleWarranties: build.query<
      ListVehicleWarrantiesApiResponse,
      ListVehicleWarrantiesApiArg
    >({
      query: (queryArg) => ({url: `/dms/v1/vehicle/${queryArg.vehicleId}/warranty`}),
      providesTags: (result, error, queryArg) => [
        {type: 'VehicleWarrantyList', id: queryArg.vehicleId},
      ],
      extraOptions: {
        responseSchema: ListVehicleWarrantiesApiResponseSchema,
      },
    }),
    assignVehicleWarranty: build.mutation<
      AssignVehicleWarrantyApiResponse,
      AssignVehicleWarrantyApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/vehicle/${queryArg.vehicleId}/warranty`,
        method: 'POST',
        body: queryArg.assignWarrantyProductRequestBody,
      }),
      invalidatesTags: invalidateOnSuccess((result, queryArg: AssignVehicleWarrantyApiArg) => [
        {type: 'VehicleWarrantyList', id: queryArg.vehicleId},
      ]),
      extraOptions: {
        requestSchema: AssignWarrantyProductRequestBodySchema,
        responseSchema: UuidResponseBodySchema,
      },
    }),
    updateVehicleWarranty: build.mutation<void, UpdateVehicleWarrantyApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/vehicle/${queryArg.vehicleId}/warranty/${queryArg.warrantyProductId}`,
        method: 'PUT',
        body: queryArg.updateWarrantyAssignmentRequestBody,
      }),
      invalidatesTags: invalidateOnSuccess((result, queryArg: UpdateVehicleWarrantyApiArg) => [
        {type: 'VehicleWarrantyList', id: queryArg.vehicleId},
      ]),
      extraOptions: {
        requestSchema: UpdateWarrantyAssignmentRequestBodySchema,
      },
    }),
    removeVehicleWarranty: build.mutation<void, RemoveVehicleWarrantyApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/vehicle/${queryArg.vehicleId}/warranty/${queryArg.warrantyProductId}`,
        method: 'DELETE',
      }),
      invalidatesTags: invalidateOnSuccess((result, queryArg: RemoveVehicleWarrantyApiArg) => [
        {type: 'VehicleWarrantyList', id: queryArg.vehicleId},
      ]),
    }),
  }),
});

export const {
  useListWarrantyProgramQuery,
  useLazyListWarrantyProgramQuery,
  useGetWarrantyProgramQuery,
  useCreateWarrantyProgramMutation,
  useUpdateWarrantyProgramMutation,
  useDeleteWarrantyProgramMutation,
  useGetSalesWarrantySettingsQuery,
  useSetSalesWarrantySettingsMutation,
  useListWarrantyTypeQuery,
  useGetWarrantyTypeQuery,
  useCreateWarrantyTypeMutation,
  useUpdateWarrantyTypeMutation,
  useDeleteWarrantyTypeMutation,
  useListVehicleWarrantiesQuery,
  useAssignVehicleWarrantyMutation,
  useUpdateVehicleWarrantyMutation,
  useRemoveVehicleWarrantyMutation,
} = warrantyApi;
